import { z } from "zod";

import { TemplatedTextSchema } from "./text";

export enum RPASessionStatus {
  /**
   * Has not been run yet
   */
  NOT_RUN = "not_run",
  /**
   * Has been run and completed successfully
   */
  SUCCESS = "success",
  /**
   * Is currently running
   */
  RUNNING = "running",
  /**
   * Has failed
   */
  ERROR = "error",
  /**
   * The system has failed
   */
  SYSTEM_ERROR = "system_error",
  /**
   * The run has been paused
   */
  PAUSED = "paused",
}

export const RPASessionStatusSchema = z.enum([
  RPASessionStatus.NOT_RUN,
  RPASessionStatus.SUCCESS,
  RPASessionStatus.RUNNING,
  RPASessionStatus.ERROR,
  RPASessionStatus.SYSTEM_ERROR,
  RPASessionStatus.PAUSED,
]);

const BaseNode = z.object({
  label: z.string(),
  prompt: TemplatedTextSchema,
  forceComputerUse: z.boolean().optional(),
});

const NavigateNode = BaseNode.extend({
  actionType: z.literal("Navigate"),
  url: z.string(),
});

const ClickNode = BaseNode.extend({
  actionType: z.literal("Click"),
  selector: z.string(),
});

const TypeNode = BaseNode.extend({
  actionType: z.literal("Type"),
  selector: z.string(),
  text: z.string(),
});

const ExtractNode = BaseNode.extend({
  actionType: z.literal("Extract"),
  selector: z.string(),
});

const ScrollNode = BaseNode.extend({
  actionType: z.literal("Scroll"),
});

export const RPANodeSchema = z.object({
  id: z.string(),
  type: z.literal("rpaNode").optional(),
  position: z.object({ x: z.number(), y: z.number() }),
  data: z.discriminatedUnion("actionType", [
    NavigateNode,
    ClickNode,
    TypeNode,
    ExtractNode,
    ScrollNode,
  ]),
});

export const RPAEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
});

export type RPANode = z.infer<typeof RPANodeSchema>;
export type RPAEdge = z.infer<typeof RPAEdgeSchema>;
